/*
 *
 * File: index.js
 *
 * Purpose: API for DrivePartner booking server access.
 *
 * Author:  Thomas Burgard (Software development)
 * Created: 23.02.2022
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import axios from "axios";
import {
  GET_USERDATA,
  GET_BOOKINGDATA,
  GET_PASSENGERDATA,
  GET_PASSENGER_BOOKINGS,
} from "./actionCodes";

// Server URL for React API
const URL_SERV = "https://api.drivepartner.com/multistop/";

// All constants declarations for PHP server API-files
const PHP_FILE_GETUSERDATA = "get_userdata.php";
const PHP_FILE_GETBOOKINGDATA = "get_bookingdata.php";
const PHP_FILE_GETPASSENGERDATA = "get_passengerdata.php"; // For passenger login!
const PHP_FILE_GET_PASSENGER_BOOKINGS = "get_passengerBookings.php";

/**
 * Get user data from PHP-Server. Send request with
 * actioncode & username to server and receive user data
 * response.
 *
 * @param {*} username
 * @returns response data (user data)
 */
export const getUserData = async (username) => {
  try {
    const response = await axios({
      method: "POST",
      url: URL_SERV + PHP_FILE_GETUSERDATA,
      data: {
        actionCode: GET_USERDATA,
        username: username,
      },
    });

    return {
      userData: response.data,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get passenger data from PHP-Server. Send request with
 * actioncode, username, paddword, driverID to server and receive
 * passenger data response or error response.
 *
 * @param {*} username username
 * @param {*} password user poassword
 * @param {*} driverID ID of driver
 * @returns response data (user data)
 */
export const getPassengerData = async (username, password, driverID) => {
  try {
    const response = await axios({
      method: "POST",
      url: URL_SERV + PHP_FILE_GETPASSENGERDATA,
      data: {
        actionCode: GET_PASSENGERDATA,
        username: username,
        password: password,
        driverID: driverID,
      },
    });

    return {
      passengerData: response.data,
      username,
    };
  } catch (error) {
    throw error;
  }
};

/**
 *  Get passenger bookings with given passenger ID, driver ID & search item input.
 *
 * @param {*} year Given year
 * @param {*} month Given month
 * @param {*} passengerID ID of passenger
 * @param {*} driverID ID of driver
 * @param {*} pageNo Number of pages to show on screen
 * @param {*} pageSize Page size (how many bookings to show on screen)
 */
export const getPassengerBookings = async (
  year,
  month,
  passengerID,
  driverID,
  pageNo,
  pageSize
) => {
  try {
    const response = await axios({
      method: "POST",
      url: URL_SERV + PHP_FILE_GET_PASSENGER_BOOKINGS,
      data: {
        actionCode: GET_PASSENGER_BOOKINGS,
        year: year,
        month: month,
        passengerID: passengerID,
        driverID: driverID,
        pageNo: pageNo,
        pageSize: pageSize,
      },
    });

    return {
      passengerBookings: response.data,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get booking data from PHP-Server with given ticket number.
 * Send request with actioncode & username to server and
 * receive user data response.
 *
 * @param {*} userID
 * @param {*} ticketNumber
 * @returns response data (user data)
 */
export const getBookingData = async (userID, ticketNumber) => {
  try {
    const response = await axios({
      method: "POST",
      url: URL_SERV + PHP_FILE_GETBOOKINGDATA,
      data: {
        actionCode: GET_BOOKINGDATA,
        userID: userID,
        ticketNumber: ticketNumber,
      },
    });

    return {
      bookingData: response.data,
    };
  } catch (error) {
    throw error;
  }
};
